import { createSlice, PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import {
  ActionType,
  AreaType,
  AvailabilityType,
  BasicDataType,
  ExtraType,
  JobSymptomType,
  OrderLineInfoType,
  PartType,
  PlannerNodesType,
  SortGroupsType,
  SupplierType,
  TechnicianType,
} from "operations/schema/schema";
import { asyncQueries, queryBuilder } from "./cache.queries";

export type AvailablePartsCache = {
  technicianParts: PartType[];
  locationalParts: PartType[];
  liveSearchParts: PartType[];
};
export type Loading = {
  prefill: boolean;
  plannerPrefill: boolean;
  availableParts: boolean;
  technicians: boolean;
  technicianParts: boolean;
  requestableParts: boolean;
  suggestedParts: boolean;
  frequencyParts: boolean;
};
export type State = {
  lastLoaded: string | null;
  availableParts: AvailablePartsCache;
  actions: ActionType[];
  areas: AreaType[];
  departments: BasicDataType[];
  technicians: TechnicianType[];
  extras: ExtraType[];
  orderLineInfo: OrderLineInfoType;
  partSortGroups: SortGroupsType;
  plannerNodes: PlannerNodesType[];
  requestableParts: PartType[];
  suggestedParts: {
    [x: string]: string[];
  };
  frequencyParts: {
    [x: string]: string[];
  };
  suppliers: SupplierType[];
  symptoms: JobSymptomType[];
  loading: Loading;
  technicianPartsLastLoaded: string | null;
  requestablePartsLastLoaded: string | null;
  locationalPartsLastLoaded: string | null;
  availabilityTypes: AvailabilityType[];
};

interface Reducers extends SliceCaseReducers<State> {
  setData: <K extends Extract<keyof State, string>>(
    state: State,
    action: PayloadAction<{
      key: K;
      data: State[K];
    }>,
  ) => State;
  setLoading: <K extends Extract<keyof Loading, string>>(
    state: State,
    action: PayloadAction<{
      key: K;
      data: Loading[K];
    }>,
  ) => State;
}

export const initialState: State = {
  lastLoaded: null,
  availableParts: {
    technicianParts: [],
    locationalParts: [],
    liveSearchParts: [],
  },
  actions: [],
  areas: [],
  departments: [],
  technicians: [],
  extras: [],
  orderLineInfo: {},
  partSortGroups: {},
  plannerNodes: [],
  requestableParts: [],
  suggestedParts: {},
  frequencyParts: {},
  suppliers: [],
  symptoms: [],
  technicianPartsLastLoaded: null,
  requestablePartsLastLoaded: null,
  locationalPartsLastLoaded: null,
  availabilityTypes: [],
  loading: {
    prefill: false,
    plannerPrefill: false,
    availableParts: false,
    technicians: false,
    technicianParts: false,
    requestableParts: false,
    suggestedParts: false,
    frequencyParts: false,
  },
};
const reducers: Reducers = {
  setData: (state, { payload: { key, data } }) => {
    state[key] = data;
    return state;
  },
  setLoading: (state, { payload: { key, data } }) => {
    state.loading[key] = data;
    return state;
  },
};

const cacheStore = createSlice<State, Reducers>({
  name: "cache",
  initialState,
  reducers,
  extraReducers: queryBuilder,
});

export default cacheStore.reducer;
export const { setData, setLoading } = cacheStore.actions;
export const {
  getCachePrefill,
  getPartnerCachePrefill,
  getPlannerCachePrefill,
  getAvailableParts,
  getTechnicians,
  getPartsTechnician,
  getRequestableParts,
  getSuggestedParts,
  getFrequencyParts,
} = asyncQueries;
